import { SkeletonRow } from '../Skeleton';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { IconSpark } from '../icons';

interface Message {
  id: string;
  authorId: string | null;
  authorName: string;
  isSparky: boolean;
  body: string;
  refs: { versionId?: string; screenRef?: string; issueId?: string };
  pending: boolean;
  createdAt: string;
}

/**
 * Project chat (spec 5b.15). Sparky is mentionable with @sparky so the guide stays inside the team
 * conversation instead of being a separate surface.
 */
export function MessagesPanel({ projectId }: { projectId: string }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[] | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ messages: Message[] }>(`/box/projects/${projectId}/messages`);
      setMessages(data.messages);
    } catch {
      setMessages([]);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages?.length]);

  const send = async () => {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    setDraft('');
    try {
      await api.post(`/box/projects/${projectId}/messages`, { body });
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (!messages)
    return (
      <div className="space-y-4 p-3" aria-busy="true" aria-label="Loading messages">
        <SkeletonRow />
        <SkeletonRow />
        <SkeletonRow />
      </div>
    );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div ref={listRef} className="min-h-0 flex-1 space-y-3 overflow-auto p-3">
        {messages.length === 0 && (
          <p className="text-sm text-muted">
            No messages yet. Anyone working on this project can talk here — mention
            <span className="font-medium"> @sparky</span> to bring the guide in.
          </p>
        )}
        {messages.map(message => (
          <div key={message.id} className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-muted">
              {message.isSparky && (
                <span className="text-accent">
                  <IconSpark width={13} height={13} />
                </span>
              )}
              <span className={message.isSparky ? 'font-medium text-accent' : 'font-medium'}>
                {message.authorId === user?.id ? 'You' : message.authorName}
              </span>
              <span>{new Date(message.createdAt).toLocaleTimeString()}</span>
              {message.pending && <span>· checking</span>}
            </div>
            <p
              className={[
                'whitespace-pre-wrap rounded-xl p-2.5 text-sm',
                message.isSparky ? 'bg-accent/10' : 'bg-page',
              ].join(' ')}
            >
              {message.body}
            </p>
            {message.refs?.versionId && (
              <p className="text-[11px] text-muted">Linked to a version</p>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-end gap-2 border-t border-line p-3">
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          rows={2}
          placeholder="Message your team — or @sparky"
          className="min-h-[48px] flex-1 resize-y rounded-lg border border-line bg-surface p-2 text-sm outline-none ring-1 ring-transparent transition focus:border-ink/50 focus:ring-ink/20"
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={busy || !draft.trim()}
          className="rounded-lg bg-accent px-3 py-2 text-sm font-medium text-accent-ink transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          Send
        </button>
      </div>
    </div>
  );
}
